/**
 * Store and hooks for Simplify Nav New User spotlight state (ACTIVATION-2059).
 * Centralizes hasOpenedCustomize so we can clean up in one place if the experiment is abandoned.
 */
import type {
	BoundActions,
	HookActionsFunction,
	HookFunction,
	Store,
	StoreActionApi,
} from 'react-sweet-state';
import { createStore, createHook, createActionsHook } from 'react-sweet-state';

import { confluenceLocalStorageInstance as localStorage } from '@confluence/storage-manager/entry-points/storageInstance.ts';
import { keys as localStorageKeys } from '@confluence/storage-manager/entry-points/LocalStorageKeys.ts';

const getInitialHasOpenedCustomize = (): boolean => {
	try {
		return (
			localStorage?.getItemAsBoolean(
				localStorageKeys.SIMPLIFY_NAV_NEW_USER_SPOTLIGHT_CUSTOMIZE_OPENED,
			) ?? false
		);
	} catch {
		return false;
	}
};

const initialState = {
	hasOpenedCustomize: getInitialHasOpenedCustomize(),
};

export const SimplifyNavNewUserSpotlightStore: Store<
	{
		hasOpenedCustomize: boolean;
	},
	{
		setHasOpenedCustomize: () => ({
			setState,
			getState,
		}: StoreActionApi<{
			hasOpenedCustomize: boolean;
		}>) => void;
	}
> = createStore({
	initialState,
	actions: {
		setHasOpenedCustomize:
			(): (({
				setState,
				getState,
			}: StoreActionApi<{
				hasOpenedCustomize: boolean;
			}>) => void) =>
			({ setState, getState }) => {
				if (getState().hasOpenedCustomize) {
					return;
				}

				setState({ hasOpenedCustomize: true });

				try {
					localStorage?.setItem(
						localStorageKeys.SIMPLIFY_NAV_NEW_USER_SPOTLIGHT_CUSTOMIZE_OPENED,
						'true',
					);
				} catch {
					// storage unavailable
				}
			},
	},
	name: 'simplifyNavNewUserSpotlightStore',
});

export type SpotlightSource = 'new-user' | 'xflow' | null;

export const useSimplifyNavNewUserSpotlightStore: HookFunction<
	boolean,
	BoundActions<
		{
			hasOpenedCustomize: boolean;
		},
		{
			setHasOpenedCustomize: () => ({
				setState,
				getState,
			}: StoreActionApi<{
				hasOpenedCustomize: boolean;
			}>) => void;
		}
	>,
	void
> = createHook(SimplifyNavNewUserSpotlightStore, {
	selector: (state) => state.hasOpenedCustomize,
});

export const useSimplifyNavNewUserSpotlightStoreActions: HookActionsFunction<
	BoundActions<
		{
			hasOpenedCustomize: boolean;
		},
		{
			setHasOpenedCustomize: () => ({
				setState,
				getState,
			}: StoreActionApi<{
				hasOpenedCustomize: boolean;
			}>) => void;
		}
	>
> = createActionsHook(SimplifyNavNewUserSpotlightStore);
